/**
 * The ring, synthesised.
 *
 * One AudioContext for the whole app, created early and unlocked on the first
 * touch anywhere. This is the entire fix for "the phone does not ring": a ring
 * arrives without a user gesture, and a context created at that moment starts
 * suspended and stays silent. A context the user already unlocked by tapping
 * anything — opening a conversation, typing — plays immediately.
 */
let shared: AudioContext | undefined;

function context(): AudioContext {
  shared ??= new AudioContext();
  if (shared.state === 'suspended') void shared.resume().catch(() => undefined);
  return shared;
}

if (typeof window !== 'undefined') {
  const unlock = () => {
    void context();
  };
  // Capture phase and never removed: cheap, and a context suspended later by
  // the platform gets nudged awake by the next interaction.
  window.addEventListener('pointerdown', unlock, { capture: true });
  window.addEventListener('keydown', unlock, { capture: true });
}

/**
 * A phone ring, not a notification blip: the classic 440+480 Hz pair, a full
 * second on, two off, at a volume meant to be heard across a room. The
 * outgoing ringback is the single 425 Hz tone phones play the caller, softer.
 */
export function startRing(kind: 'incoming' | 'outgoing'): () => void {
  const ctx = context();
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);

  const oscillators = (kind === 'incoming' ? [440, 480] : [425]).map((frequency) => {
    const osc = ctx.createOscillator();
    osc.frequency.value = frequency;
    osc.connect(gain);
    osc.start();
    return osc;
  });

  const level = kind === 'incoming' ? 0.3 : 0.12;
  const on = kind === 'incoming' ? 1.2 : 1.0;
  const period = kind === 'incoming' ? 3.2 : 4.0;
  const cadence = () => {
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    // A short attack so the tone does not click on.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.02);
    gain.gain.setValueAtTime(level, now + on);
    gain.gain.linearRampToValueAtTime(0, now + on + 0.04);
  };
  cadence();
  const timer = setInterval(cadence, period * 1000);

  const vibrate =
    kind === 'incoming'
      ? setInterval(() => navigator.vibrate?.([600, 300, 600]), 3200)
      : undefined;
  if (kind === 'incoming') navigator.vibrate?.([600, 300, 600]);

  return () => {
    clearInterval(timer);
    if (vibrate) clearInterval(vibrate);
    for (const osc of oscillators) osc.stop();
    gain.disconnect();
    navigator.vibrate?.(0);
  };
}
